import { createTRPCClient, httpBatchLink } from "@trpc/client";
import superjson from "superjson";

import type { AppRouter } from "@samphire/api";

import { api } from "./api";
import { config } from "./config";

// Note: Shopify operations are now handled server-side via tRPC for security
// The token is kept on the backend and not exposed to the client

// Create a vanilla tRPC client for non-React usage
const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: config.isDevelopment ? `${config.webUrl}/trpc` : config.apiUrl,
      transformer: superjson,
    }),
  ],
});

// Export the React hooks for use in components
export { api };

// Types for our products
export interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  handle: string;
  images: Array<{
    id: string;
    url: string;
    altText?: string;
  }>;
  variants: Array<{
    id: string;
    title: string;
    price: {
      amount: string;
      currencyCode: string;
    };
    compareAtPrice?: {
      amount: string;
      currencyCode: string;
    } | null;
    availableForSale: boolean;
  }>;
  vendor: string;
  productType: string;
  tags: string[];
}

export interface ShopifyCart {
  id: string;
  webUrl: string;
}

// Helper functions that now use tRPC API calls
// These maintain backwards compatibility while using the secure backend API
export async function getProducts(limit = 20): Promise<ShopifyProduct[]> {
  try {
    // @ts-ignore - Types will be available after API build
    return await trpcClient.shopify.getProducts.query({ limit });
  } catch (error) {
    console.error("Error fetching products:", error);
    return [];
  }
}

export async function getProductByHandle(
  handle: string,
): Promise<ShopifyProduct | null> {
  try {
    // @ts-ignore - Types will be available after API build
    return await trpcClient.shopify.getProductByHandle.query({ handle });
  } catch (error) {
    console.error("Error fetching product:", error);
    return null;
  }
}

export async function createCartAndGetCheckoutUrl(
  variantId: string,
  quantity = 1,
  userId?: string,
  email?: string,
): Promise<string | null> {
  console.log(
    "⚠️ createCartAndGetCheckoutUrl",
    variantId,
    quantity,
    userId,
    email,
  );
  try {
    // @ts-ignore - Types will be available after API build
    return await trpcClient.shopify.createCartAndGetCheckoutUrl.mutate({
      variantId,
      quantity,
      userId,
      email,
    });
  } catch (error) {
    console.error("Error creating cart:", error);
    return null;
  }
}

export function formatPrice(amount: string, currencyCode: string): string {
  const price = parseFloat(amount);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
  }).format(price);
}
